import type { TokenSource } from "./api";

/** The slice of Firebase `Auth` the token source needs, so tests can supply a fake. */
export interface AuthLike {
  currentUser: { getIdToken(forceRefresh?: boolean): Promise<string> } | null;
  authStateReady(): Promise<void>;
}

/**
 * The api's token source: waits for Firebase to restore the session on first load, then asks
 * the current user for an ID token (Firebase refreshes it when it is close to expiry).
 */
export function firebaseTokenSource(auth: AuthLike): TokenSource {
  return async (forceRefresh = false) => {
    await auth.authStateReady();
    const user = auth.currentUser;
    return user === null ? null : user.getIdToken(forceRefresh);
  };
}

/**
 * The Auth emulator's fake Google ID token: an unsigned JSON claim set, which only the emulator
 * accepts (real Firebase Auth rejects it). Lets local dev and the app journeys sign in without
 * the emulator's popup widget, whose hand-back to the opener races its iframe.
 */
export function emulatorGoogleIdToken(email: string, name: string): string {
  const normalized = email.trim().toLowerCase();
  const claims = { sub: `emu-${normalized}`, email: normalized, email_verified: true };
  return JSON.stringify(name.trim().length === 0 ? claims : { ...claims, name: name.trim() });
}
